import React, { memo, useCallback, useState } from 'react'
import styles from './FieldHints.module.sass'
import Field from '../../../controls/Field/Field'
import clsx from 'clsx'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import Popover from '../../../etc/Popover'
import Select, { SelectProps } from '../../lists/Select/Select'

interface Props<T> extends SelectProps<T> {
    disabled?: boolean,
    multi?: boolean,
    onChange?: (text: string, reset: boolean) => void,
    children: (props: {
        isOpen: boolean,
        toggle: () => void,
    }) => JSX.Element,
}

function FieldHints<T>({...props}: Props<T>) {
    const { toClassName, multi, onClick } = props
    const [open, setOpen] = useState(false)
    const [text, setText] = useState('')

    const toClassNameFn = useCallback(
        (item: T) => clsx(styles.item, toClassName?.(item))
        , [toClassName]
    )

    const onClickFn = useCallback((item: T) => {
        !multi && setOpen(false)
        setText('')
        onClick?.(item)
    }, [multi, onClick])

    return <Popover
        isOpen={open}
        onClickOutside={event => {
            event.stopImmediatePropagation()
            setOpen(false)
        }}
        content={state => <ShadowContainer
            state={state}
            className={clsx(props.className, styles.popover, styles[state.position])}
        >
            {
                props.onChange && <Field
                    className={styles.field}
                    block
                    type={'text'}
                    autoFocus
                    value={text}
                    onChange={text => {
                        setText(text)
                        props.onChange?.(text, false)
                    }}
                />
            }
            <InfiniteScroll onReach={() => props.onChange?.(text, false)}>
                <Select<T>
                    data={props.data}
                    multi={props.multi}
                    selected={props.selected}
                    render={props.render}
                    toKey={props.toKey}
                    toClassName={toClassNameFn}
                    onClick={onClickFn}
                />
            </InfiniteScroll>
        </ShadowContainer>}
    >
        {
            props.children({
                isOpen: open,
                toggle: () => {
                    if(!props.disabled) {
                        !open && props.onChange?.(text, true)
                        setOpen(!open)
                    }
                },
            })
        }
    </Popover>
}

export default memo(FieldHints) as typeof FieldHints